// ─── Delimited text helpers (RFC 4180) ───────────────────────────────────
// Customer uploads routinely contain quoted fields holding commas, quotes and
// newlines (e.g. encrypted national IDs serialised as Python byte strings).
// Splitting on the raw delimiter shifts every column after such a field, so all
// parsing goes through parseDelimited and all writing through csvEscape.

/** Sniff the delimiter from the first physical line of a paste/upload. */
export function sniffDelim(text: string): string {
  const first = text.split(/\r?\n/, 1)[0] || ''
  return first.includes('\t') ? '\t' : first.includes('|') ? '|' : ','
}

/**
 * Parse delimited text into rows of fields, honouring RFC 4180 quoting:
 * a quoted field may contain the delimiter, CR/LF, and "" for a literal quote.
 * Unquoted fields are trimmed; quoted fields are preserved verbatim.
 */
export function parseDelimited(text: string, delim: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false   // this field opened with a quote
  let inQuotes = false // currently inside the quoted section
  const pushField = () => { row.push(quoted ? field : field.trim()); field = ''; quoted = false }
  const pushRow = () => { pushField(); rows.push(row); row = [] }

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } // escaped quote
        else inQuotes = false                          // closing quote
      } else field += ch
      continue
    }
    if (ch === '"' && field.trim() === '') { field = ''; quoted = true; inQuotes = true; continue }
    if (ch === delim) { pushField(); continue }
    if (ch === '\r' || ch === '\n') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      pushRow()
      continue
    }
    field += ch
  }
  if (field !== '' || quoted || row.length > 0) pushRow()
  return rows.filter(r => r.length > 1 || r[0] !== '') // drop blank trailing lines
}

/** Split delimited text into a header row plus data rows. */
export function parseDelimitedTable(text: string, delim?: string): { headers: string[]; rows: string[][] } {
  const src = text.replace(/^﻿/, '')
  if (!src.trim()) return { headers: [], rows: [] }
  const d = delim || sniffDelim(src)
  const parsed = parseDelimited(src, d)
  return { headers: parsed[0] || [], rows: parsed.slice(1) }
}

/** Quote a value for CSV output per RFC 4180 (quotes doubled, quoted when needed). */
export function csvEscape(v: string): string {
  const s = v == null ? '' : String(v)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Build a full CSV document from a header row and data rows. */
export function toCsv(header: string[], rows: string[][]): string {
  return [header.join(','), ...rows.map(r => r.map(csvEscape).join(','))].join('\r\n')
}

// ─── Export integrity checks ─────────────────────────────────────────────
// A vendor CSV is handed straight to a third-party portal, so a silently
// truncated or column-shifted file costs a POC cycle. Every export is verified
// against the count declared in DealHub and re-parsed before it can leave.

export interface CsvIssue {
  level: 'error' | 'warn'
  message: string
}

export interface ExportCheckInput {
  /** Header row of the customer's uploaded file. */
  sourceHeaders: string[]
  /** Data rows parsed from the customer's uploaded file. */
  sourceRows: string[][]
  /** Header row of the vendor CSV about to be written. */
  outHeader: string[]
  /** Mapped data rows of the vendor CSV about to be written. */
  outRows: string[][]
  /** Record count declared for this country in DealHub, if any. */
  declared?: number | null
}

/**
 * Verify a generated CSV survives a round-trip through a standard parser.
 * Returns how many rows a consumer would actually read, and where it breaks.
 */
export function verifyCsvRoundTrip(header: string[], rows: string[][]): {
  ok: boolean
  readableRows: number
  firstBadRow: number | null
} {
  const reparsed = parseDelimitedTable(toCsv(header, rows))
  let firstBadRow: number | null = null
  for (let i = 0; i < reparsed.rows.length; i++) {
    if (reparsed.rows[i].length !== header.length) { firstBadRow = i + 1; break }
  }
  if (firstBadRow === null && reparsed.rows.length !== rows.length) {
    firstBadRow = Math.min(reparsed.rows.length, rows.length) + 1
  }
  return {
    ok: firstBadRow === null && reparsed.rows.length === rows.length,
    readableRows: reparsed.rows.length,
    firstBadRow,
  }
}

/** Format a short list of row numbers for a warning message. */
function listRows(nums: number[]): string {
  const shown = nums.slice(0, 5).join(', ')
  return nums.length > 5 ? `${shown} (+${nums.length - 5} more)` : shown
}

/** Run every pre-download check for one country's vendor export. */
export function checkExport({ sourceHeaders, sourceRows, outHeader, outRows, declared }: ExportCheckInput): CsvIssue[] {
  const issues: CsvIssue[] = []

  // 1. Declared record count vs what the uploaded file actually contains.
  if (declared != null && declared > 0 && declared !== sourceRows.length) {
    const diff = sourceRows.length - declared
    issues.push({
      level: 'error',
      message: `DealHub says ${declared.toLocaleString()} records were supplied, but the uploaded file has ${sourceRows.length.toLocaleString()} (${diff > 0 ? '+' : ''}${diff.toLocaleString()}). Fix the upload or update the declared count before sending.`,
    })
  }

  // 2. Ragged source rows — the signature of a column shift in the upload.
  if (sourceHeaders.length > 0) {
    const ragged = sourceRows
      .map((r, i) => (r.length === sourceHeaders.length ? 0 : i + 1))
      .filter(Boolean)
    if (ragged.length > 0) {
      issues.push({
        level: 'error',
        message: `${ragged.length} uploaded row${ragged.length === 1 ? '' : 's'} do not have ${sourceHeaders.length} columns, so their fields are misaligned — row${ragged.length === 1 ? '' : 's'} ${listRows(ragged)}.`,
      })
    }
  }

  // 3. The generated file must re-parse to exactly the rows we think we wrote.
  const trip = verifyCsvRoundTrip(outHeader, outRows)
  if (!trip.ok) {
    issues.push({
      level: 'error',
      message: `Generated CSV does not re-parse cleanly: a reader gets ${trip.readableRows.toLocaleString()} of ${outRows.length.toLocaleString()} rows${trip.firstBadRow ? `, breaking at row ${trip.firstBadRow}` : ''}.`,
    })
  }

  // 4. Rows that mapped to nothing at all would be silently dropped by a vendor.
  const empty = outRows.map((r, i) => (r.some(v => v.trim() !== '') ? 0 : i + 1)).filter(Boolean)
  if (empty.length > 0) {
    issues.push({
      level: 'warn',
      message: `${empty.length} row${empty.length === 1 ? '' : 's'} mapped to entirely empty values — row${empty.length === 1 ? '' : 's'} ${listRows(empty)}.`,
    })
  }

  return issues
}
